import { resolveCourseRouteGroupSlug } from '../utils/resolve-course-route-group-slug'
import type { CPNavigationInput } from '../types/navigation'
import type { CpBreadcrumbRouteDef, CpBreadcrumbRouteLike, CpBreadcrumbSegmentDef } from '../types/cp-breadcrumb'

const dash = (): CPNavigationInput => ({ name: 'clientportal-dashboard' })

const segHome: CpBreadcrumbSegmentDef = {
  labelKey: 'clientportal.breadcrumb.home',
  navigate: dash,
}

const segCommunitiesNav: CpBreadcrumbSegmentDef = {
  labelKey: 'clientportal.navigation.communities',
  navigate: () => ({ name: 'clientportal-communities' }),
}

const segCoursesNav: CpBreadcrumbSegmentDef = {
  labelKey: 'clientportal.navigation.courses',
  navigate: () => ({ name: 'clientportal-courses' }),
}

/** Group tab pages (Discussion, Members, Events, …): same shell before name+tab injection. */
const defCommunitiesGroupTabShell: CpBreadcrumbRouteDef = {
  segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.groupHome' }],
}

const groupParams = (route: CpBreadcrumbRouteLike): Record<string, string> => {
  const groupSlug = route.params.groupSlug
  return typeof groupSlug === 'string' && groupSlug ? { groupSlug } : {}
}

/**
 * Logical route keys → segment definitions. Keys are matched via {@link resolveCpBreadcrumbRegistryKey}.
 */
export const CP_BREADCRUMB_REGISTRY: Record<string, CpBreadcrumbRouteDef> = {
  'clientportal-dashboard': {
    segments: [{ labelKey: 'clientportal.navigation.dashboard' }],
  },

  'clientportal-courses': {
    segments: [segHome, { labelKey: 'clientportal.navigation.courses' }],
  },

  'clientportal-courses-my-courses': {
    segments: [segHome, segCoursesNav, { labelKey: 'clientportal.breadcrumb.myCourses' }],
  },

  'clientportal-courses-discover': {
    segments: [segHome, segCoursesNav, { labelKey: 'clientportal.breadcrumb.discover' }],
  },

  'clientportal-communities': {
    segments: [segHome, { labelKey: 'clientportal.navigation.communities' }],
  },

  'clientportal-communities-my-communities': {
    segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.myCommunities' }],
  },

  'clientportal-communities-discover': {
    segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.discover' }],
  },

  'communities-groups-members': defCommunitiesGroupTabShell,
  'communities-groups-events': defCommunitiesGroupTabShell,
  'communities-groups-settings': defCommunitiesGroupTabShell,
  'communities-groups-learning': defCommunitiesGroupTabShell,
  'communities-groups-about': defCommunitiesGroupTabShell,
  'communities-groups-leaderboard': defCommunitiesGroupTabShell,

  'clientportal-shared-files': {
    segments: [segHome, { labelKey: 'clientportal.navigation.sharedFiles' }],
  },

  'clientportal-invoices': {
    segments: [segHome, { labelKey: 'clientportal.navigation.invoices' }],
  },

  'clientportal-estimates': {
    segments: [segHome, { labelKey: 'clientportal.navigation.estimates' }],
  },

  'clientportal-contracts': {
    segments: [segHome, { labelKey: 'clientportal.navigation.contracts' }],
  },

  'clientportal-appointments': {
    segments: [segHome, { labelKey: 'clientportal.navigation.appointments' }],
  },

  'clientportal-appointments-book': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.appointments', navigate: () => ({ name: 'clientportal-appointments' }) },
      { labelKey: 'clientportal.breadcrumb.bookAppointment' },
    ],
  },

  'clientportal-events': {
    segments: [segHome, { labelKey: 'clientportal.navigation.events' }],
  },

  'clientportal-events-live': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.events', navigate: () => ({ name: 'clientportal-events' }) },
      { labelKey: 'clientportal.breadcrumb.eventsLive' },
    ],
  },

  'clientportal-events-upcoming': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.events', navigate: () => ({ name: 'clientportal-events' }) },
      { labelKey: 'clientportal.breadcrumb.eventsUpcoming' },
    ],
  },

  'clientportal-events-past': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.events', navigate: () => ({ name: 'clientportal-events' }) },
      { labelKey: 'clientportal.breadcrumb.eventsPast' },
    ],
  },

  'clientportal-events-detail': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.events', navigate: () => ({ name: 'clientportal-events' }) },
      { labelKey: 'clientportal.breadcrumb.eventDetail' },
    ],
  },

  'clientportal-affiliates': {
    segments: [segHome, { labelKey: 'clientportal.navigation.affiliates' }],
  },

  'clientportal-affiliates-campaign': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesCampaign' },
    ],
  },

  'clientportal-affiliates-sub-affiliate': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesSubAffiliate' },
    ],
  },

  'clientportal-affiliates-lead': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesLead' },
    ],
  },

  'clientportal-affiliates-commission': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesCommission' },
    ],
  },

  'clientportal-affiliates-payout-methods': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesPayoutMethods' },
    ],
  },

  'clientportal-affiliates-media': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      { labelKey: 'clientportal.breadcrumb.affiliatesMedia' },
    ],
  },

  'clientportal-affiliates-media-folder': {
    segments: [
      segHome,
      { labelKey: 'clientportal.navigation.affiliates', navigate: () => ({ name: 'clientportal-affiliates' }) },
      {
        labelKey: 'clientportal.breadcrumb.affiliatesMedia',
        navigate: () => ({ name: 'clientportal-affiliates-media' }),
      },
      { labelKey: 'clientportal.breadcrumb.folder' },
    ],
  },

  'communities-groups-home': {
    segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.groupHome' }],
  },

  'communities-groups-channel': {
    segments: [
      segHome,
      segCommunitiesNav,
      {
        labelKey: 'clientportal.breadcrumb.groupHome',
        navigate: route => {
          const p = groupParams(route)
          return p.groupSlug ? { name: 'communities-groups-home', params: p } : null
        },
      },
      { labelKey: 'clientportal.breadcrumb.channel' },
    ],
  },

  'communities-groups-home-post': {
    segments: [
      segHome,
      segCommunitiesNav,
      {
        labelKey: 'clientportal.breadcrumb.groupHome',
        navigate: route => {
          const p = groupParams(route)
          return p.groupSlug ? { name: 'communities-groups-home', params: p } : null
        },
      },
      { labelKey: 'clientportal.breadcrumb.post' },
    ],
  },

  'communities-groups-channel-post': {
    segments: [
      segHome,
      segCommunitiesNav,
      {
        labelKey: 'clientportal.breadcrumb.groupHome',
        navigate: route => {
          const p = groupParams(route)
          return p.groupSlug ? { name: 'communities-groups-home', params: p } : null
        },
      },
      {
        labelKey: 'clientportal.breadcrumb.channel',
        navigate: route => {
          const gs = route.params.groupSlug
          const cs = route.params.channelSlug
          if (typeof gs !== 'string' || typeof cs !== 'string') return null
          return { name: 'communities-groups-channel', params: { groupSlug: gs, channelSlug: cs } }
        },
      },
      { labelKey: 'clientportal.breadcrumb.post' },
    ],
  },

  'communities-conversations': {
    segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.conversations' }],
  },

  'communities-conversations-conversationId': {
    segments: [
      segHome,
      segCommunitiesNav,
      {
        labelKey: 'clientportal.breadcrumb.conversations',
        navigate: () => ({ name: 'communities-conversations' }),
      },
      { labelKey: 'clientportal.breadcrumb.conversation' },
    ],
  },

  'communities-user': {
    segments: [segHome, segCommunitiesNav, { labelKey: 'clientportal.breadcrumb.memberProfile' }],
  },

  'courses-search': {
    segments: [segHome, segCoursesNav, { labelKey: 'clientportal.breadcrumb.search' }],
  },

  'courses-products': {
    segments: [segHome, segCoursesNav, { labelKey: 'clientportal.breadcrumb.course' }],
  },

  'courses-products-modules-lessons': {
    segments: [
      segHome,
      segCoursesNav,
      {
        labelKey: 'clientportal.breadcrumb.course',
        navigate: route => {
          const productId = route.params.productId
          if (typeof productId !== 'string') return null
          const params: Record<string, string> = { productId }
          const gs = resolveCourseRouteGroupSlug(route)
          if (gs) params.groupSlug = gs
          return { name: 'courses-products', params }
        },
      },
      { labelKey: 'clientportal.breadcrumb.lesson' },
    ],
  },

  'my-library': {
    segments: [segHome, segCoursesNav, { labelKey: 'clientportal.breadcrumb.myLibrary' }],
  },

  'account-settings': {
    segments: [
      segHome,
      {
        labelKey: 'account.web.title',
        navigate: () => ({ name: 'account-settings', query: { activeTab: 'profile' } }),
      },
      { labelKey: 'account.web.sidebar.profile' },
    ],
  },
}

interface PathRule {
  key: keyof typeof CP_BREADCRUMB_REGISTRY | string
  test: (path: string, route: CpBreadcrumbRouteLike) => boolean
}

/**
 * First matching rule wins (most specific rules must appear first).
 */
const CP_BREADCRUMB_PATH_RULES: PathRule[] = [
  {
    key: 'account-settings',
    test: path => /^\/account(\/|$)/.test(path),
  },
  {
    key: 'clientportal-affiliates-media-folder',
    test: path => /^\/affiliates\/media\/folder\/[^/]+\/?$/.test(path),
  },
  {
    key: 'courses-products-modules-lessons',
    test: path =>
      /\/courses\/products\/[^/]+\/modules\/[^/]+\/lessons\/[^/]+\/?$/.test(path) ||
      /\/learning\/courses\/products\/[^/]+\/modules\/[^/]+\/lessons\/[^/]+\/?$/.test(path),
  },
  {
    key: 'courses-products',
    test: (path, route) => {
      if (!/\/courses\/products\/[^/]+\/?$/.test(path) && !/\/learning\/courses\/[^/]+\/?$/.test(path)) {
        return false
      }
      if (path.includes('/modules/') || path.includes('/lessons/')) return false
      return typeof route.params.productId === 'string'
    },
  },
  {
    key: 'courses-search',
    test: path => /^\/courses\/search/.test(path),
  },
  {
    key: 'clientportal-courses-my-courses',
    test: path => /^\/courses\/my-courses\/?$/.test(path),
  },
  {
    key: 'clientportal-courses-discover',
    test: path => /^\/courses\/discover\/?$/.test(path),
  },
  {
    key: 'clientportal-courses',
    test: path => /^\/courses\/?$/.test(path),
  },
  {
    key: 'clientportal-communities-my-communities',
    test: path => /^\/communities\/my-communities\/?$/.test(path),
  },
  {
    key: 'clientportal-communities-discover',
    test: path => /^\/communities\/discover\/?$/.test(path),
  },
  {
    key: 'clientportal-communities',
    test: path => /^\/communities\/?$/.test(path) && !path.includes('/groups/') && !path.includes('/users/'),
  },
  {
    key: 'communities-groups-members',
    test: path =>
      /^\/communities\/groups\/[^/]+\/members\/?$/.test(path) || (!path.startsWith('/communities/') && /^\/[^/]+\/members\/?$/.test(path)),
  },
  {
    key: 'communities-groups-events',
    test: path =>
      /^\/communities\/groups\/[^/]+\/events(\/|$)/.test(path) ||
      (!path.startsWith('/communities/') && /^\/[^/]+\/events(\/|$)/.test(path)),
  },
  {
    key: 'communities-groups-settings',
    test: path =>
      /^\/communities\/groups\/[^/]+\/settings(\/|$)/.test(path) ||
      (!path.startsWith('/communities/') && /^\/[^/]+\/settings(\/|$)/.test(path)),
  },
  {
    key: 'communities-groups-learning',
    test: path =>
      /^\/communities\/groups\/[^/]+\/learning(\/|$)/.test(path) ||
      (!path.startsWith('/communities/') && /^\/[^/]+\/learning(\/|$)/.test(path)),
  },
  {
    key: 'communities-groups-about',
    test: path =>
      /^\/communities\/groups\/[^/]+\/about\/?$/.test(path) || (!path.startsWith('/communities/') && /^\/[^/]+\/about\/?$/.test(path)),
  },
  {
    key: 'communities-groups-leaderboard',
    test: path =>
      /^\/communities\/groups\/[^/]+\/leaderboard\/?$/.test(path) ||
      (!path.startsWith('/communities/') && /^\/[^/]+\/leaderboard\/?$/.test(path)),
  },
  {
    key: 'communities-groups-channel-post',
    test: path =>
      /^\/communities\/groups\/[^/]+\/channels\/[^/]+\/posts\/[^/]+\/?$/.test(path) ||
      (/\/channels\/[^/]+\/posts\/[^/]+\/?$/.test(path) && !!path.match(/^\/[^/]+\//)),
  },
  {
    key: 'communities-groups-home-post',
    test: path => /^\/communities\/groups\/[^/]+\/home\/posts\/[^/]+\/?$/.test(path) || /^\/[^/]+\/home\/posts\/[^/]+\/?$/.test(path),
  },
  {
    key: 'communities-groups-channel',
    test: path => /^\/communities\/groups\/[^/]+\/channels\/[^/]+\/?$/.test(path) || /^\/[^/]+\/channels\/[^/]+\/?$/.test(path),
  },
  {
    key: 'communities-groups-home',
    test: path => /^\/communities\/groups\/[^/]+\/home\/?$/.test(path) || /^\/[^/]+\/home\/?$/.test(path),
  },
  {
    key: 'communities-conversations-conversationId',
    test: path => /^\/communities\/conversations\/[^/]+\/?$/.test(path) || /^\/conversations\/[^/]+\/?$/.test(path),
  },
  {
    key: 'communities-conversations',
    test: path => /^\/communities\/conversations\/?$/.test(path) || /^\/conversations\/?$/.test(path),
  },
  {
    key: 'communities-user',
    test: path =>
      /\/communities\/users\/[^/]+/.test(path) ||
      /\/users\/[^/]+/.test(path) ||
      /\/communities\/user\/[^/]+/.test(path) ||
      /\/user\/[^/]+/.test(path),
  },
  {
    key: 'my-library',
    test: path => /^\/courses\/my-library\/?$/.test(path) || /^\/communities\/my-library\/?$/.test(path),
  },
  {
    key: 'clientportal-dashboard',
    test: path => /^\/dashboard\/?$/.test(path),
  },
  {
    key: 'clientportal-shared-files',
    test: path => /^\/shared-files\/?$/.test(path),
  },
  {
    key: 'clientportal-invoices',
    test: path => /^\/invoices\/?$/.test(path),
  },
  {
    key: 'clientportal-estimates',
    test: path => /^\/estimates\/?$/.test(path),
  },
  {
    key: 'clientportal-contracts',
    test: path => /^\/contracts\/?$/.test(path),
  },
  {
    key: 'clientportal-appointments-book',
    test: path => /^\/appointments\/book\/?$/.test(path),
  },
  {
    key: 'clientportal-appointments',
    test: path => /^\/appointments\/?$/.test(path),
  },
  {
    key: 'clientportal-events',
    test: path => /^\/events\/?$/.test(path),
  },
  {
    key: 'clientportal-events-live',
    test: path => /^\/events\/live\/?$/.test(path),
  },
  {
    key: 'clientportal-events-upcoming',
    test: path => /^\/events\/upcoming\/?$/.test(path),
  },
  {
    key: 'clientportal-events-past',
    test: path => /^\/events\/past\/?$/.test(path),
  },
  {
    key: 'clientportal-events-detail',
    test: path => /^\/events\/[^/]+\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-campaign',
    test: path => /^\/affiliates\/campaign\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-sub-affiliate',
    test: path => /^\/affiliates\/sub-affiliate\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-lead',
    test: path => /^\/affiliates\/lead\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-commission',
    test: path => /^\/affiliates\/commission\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-payout-methods',
    test: path => /^\/affiliates\/payout-methods\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates-media',
    test: path => /^\/affiliates\/media\/?$/.test(path),
  },
  {
    key: 'clientportal-affiliates',
    test: path => /^\/affiliates\/?$/.test(path),
  },
]

export function resolveCpBreadcrumbRegistryKey(route: CpBreadcrumbRouteLike): string | null {
  const name = route.name
  if (typeof name === 'string' && CP_BREADCRUMB_REGISTRY[name]) {
    return name
  }

  const path = route.path
  for (const rule of CP_BREADCRUMB_PATH_RULES) {
    if (rule.test(path, route)) {
      return rule.key
    }
  }
  return null
}

export function getCpBreadcrumbDefinition(key: string): CpBreadcrumbRouteDef | undefined {
  return CP_BREADCRUMB_REGISTRY[key]
}
